/**
 * 创意设计中心卡墙 —— **按目的分组**(V1 §3.3,2026-07-25;组数随分组表走,这里不写死)。
 *
 * ═══ 改之前是什么样,为什么要改 ═══════════════════════════════════════════════
 * 三节:商品出图 3(形制:1 张 / N 张 / 3 比例)· 长图工坊 10(目的:电商详情 / 企业介绍 / …)
 * · 横宽 1(形制 + 8 投放位)。**三节是三个不同维度** —— 新用户得先学会「主图 / 组图 /
 * 套图 / 长图 / 宽图」这套产品术语,才找得到自己要的东西。而首页发现流刚把 17 舱收成
 * 5 群横滑带,舱内页却退回按产物形制平铺的长墙,语言不接。
 *
 * 现在维度统一成**目的**(分组表单源 `entities/scene-card/model/posterPurpose.ts`);
 * 组名与组内卡全在那张表里,本文件只负责怎么摆。形制没被删,只是降级 ——
 * 它仍以几何图示留在卡面上(长图裁 4:3 看不出是长图,那一维只有图示表达得了),
 * 但不再当分组依据。
 *
 * ★组副标说的是**该组的判断依据**(什么时候选这组)。原来那句「点卡带预设进创建页 ·
 *   样图为真产物」是实现语言,用户读完仍不知道该不该点 —— `posterPurpose.test.ts` 锁死禁用词。
 * ★路由、preset、引擎**零改动**:点卡仍是 navigate(route, { state: { scenePreset } })。
 *   这一波动的只是卡怎么摆、怎么说。
 */
import { useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  POSTER_PURPOSE_GROUPS,
  POSTER_RECOMMENDED_CARD_KEY,
  posterCardsOfGroup,
} from "@/entities/scene-card/model/posterPurpose";
import type { SceneCard } from "@/entities/scene-card/model/types";
import { SceneCapCard } from "@/entities/scene-card/ui/SceneCapCard";
import { useStaggerReveal } from "@/shared/motion/useStaggerReveal";
import { PosterFreeCard } from "./PosterFreeCard";
import { PosterSourceEntries } from "./PosterSourceEntries";

function SectionHead({ title, sub, count }: { title: string; sub: string; count?: number }) {
  return (
    <div className="mb-3 flex flex-wrap items-baseline gap-2">
      <span className="h-3.5 w-[3px] self-center rounded-sm bg-primary" />
      <h2 className="text-sm font-semibold">{title}</h2>
      <span className="text-xs text-muted-foreground">{sub}</span>
      {count !== undefined && (
        <span className="ml-auto text-2xs text-muted-foreground">{count} 种</span>
      )}
    </div>
  );
}

export function PosterHubSections() {
  const navigate = useNavigate();
  const open = (card: SceneCard) => {
    if (!card.route) return;
    navigate(card.route, card.preset ? { state: { scenePreset: card.preset } } : undefined);
  };
  return (
    <div className="space-y-6">
      {/* R2①:「这里能出」hero 删除 —— 形制已由卡面徽章表达,页头 PageHeader 的副标
          已说清楚这舱是干嘛的;首屏让给来源入口条与卡墙(hero 那 ~140px 是纯重复)。 */}
      {/* 三条「我已经有料了」的路 —— 替代原来的三个页头按钮 */}
      <PosterSourceEntries />

      {/* 2026-07-31 卡墙改版:文生图 / 图生图从页底提到首位。
          ★改之前它们是**兜底**(「都不是?」),摆在 14 张场景卡后面 —— 前提是「用户已经知道
            自己要什么,才轮得到自己描述」。卡墙收窄之后这个前提不成立了:落不进任何一张卡的
            需求占多数,自己描述反而是**最常走的那条路**,不该藏在最后。
          ★2026-08-05 放回 4 张后仍留在首位:8 张场景卡照样盖不全需求,位置由「哪条路最常走」
            决定,不由墙上有几张卡决定。 */}
      <FreeCreateSection
        onOpen={() => navigate("/ai-haibao/create?tab=text")}
        onOpenI2I={() => navigate("/ai-haibao/create?tab=i2i")}
      />

      {POSTER_PURPOSE_GROUPS.map((group) => (
        <PurposeGroupSection
          key={group.key}
          groupKey={group.key}
          title={group.title}
          sub={group.sub}
          onOpen={open}
        />
      ))}
    </div>
  );
}

function PurposeGroupSection({
  groupKey,
  title,
  sub,
  onOpen,
}: {
  groupKey: string;
  title: string;
  sub: string;
  onOpen: (card: SceneCard) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useStaggerReveal(ref);
  const cards = posterCardsOfGroup(groupKey);

  return (
    <section className="scene-wall" data-purpose-group={groupKey}>
      <SectionHead title={title} sub={sub} count={cards.length} />
      <div ref={ref} className="scene-grid">
        {cards.map((c, i) => (
          <SceneCapCard
            key={c.key}
            card={c}
            onOpen={() => onOpen(c)}
            recommended={c.key === POSTER_RECOMMENDED_CARD_KEY}
            index={i}
          />
        ))}
        {/* R2①:「直接选一个投放位」8 尺寸 chips 长条删除 —— 它是搭建器「投放在哪儿」
            面板的复制品(点卡进去第一件事就是选投放位),hub 再铺一遍是两份真相;
            卡面 tags 的「8 尺寸预设」保留信息气味。 */}
      </div>
    </section>
  );
}

/**
 * 自由创作两张卡(文生图 / 图生图)。
 *
 * ★`data-purpose-group="fallback"` 这个值**故意不改**:E2E 与单测按它定位这一段,
 *   改成 "free" 只会让一批选择器一起红,而它标识的是同一段内容。位置变了,身份没变。
 */
function FreeCreateSection({ onOpen, onOpenI2I }: { onOpen: () => void; onOpenI2I: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useStaggerReveal(ref);
  return (
    <section className="scene-wall" data-purpose-group="fallback">
      {/* 2026-07-28 方案 B:改图从创建页的一个 tab 独立成这里的第二张卡 —— 它是
          「底图从哪来」,跟上面那些「产出形态」不是一个维度 */}
      <SectionHead title="先来一张" sub="自己描述,或拿一张现成的图来改" />
      <div ref={ref} className="scene-grid">
        <PosterFreeCard onOpen={onOpen} />
        <PosterFreeCard variant="i2i" onOpen={onOpenI2I} />
      </div>
    </section>
  );
}
